import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, Eye, Trash2, AlertCircle, Filter, TrendingUp, TrendingDown } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import { profitCalculationService } from '../../services/profitCalculationService';
import AdminHeader from '../admin/components/AdminHeader';
import Button from '../../components/ui/Button';


import CalculationDetailModal from './components/CalculationDetailModal';
import CalculationFilters from './components/CalculationFilters';

const ProfitCalculationHistory = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const [calculations, setCalculations] = useState([]);
  const [filteredCalculations, setFilteredCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCalculation, setSelectedCalculation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    projectName: '',
    profitStatus: 'all' // 'all', 'profit', 'loss'
  });

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (profile?.role !== 'admin') {
        navigate('/projeto');
      } else {
        loadCalculations();
      }
    }
  }, [user, profile, authLoading, navigate]);

  useEffect(() => {
    applyFilters();
  }, [calculations, filters]);

  const loadCalculations = async () => {
    try {
      setLoading(true);
      const data = await profitCalculationService?.getAllCalculations();
      setCalculations(data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar histórico: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...calculations];

    // Filter by date range
    if (filters?.dateFrom) {
      filtered = filtered?.filter(calc => 
        new Date(calc?.createdAt) >= new Date(filters?.dateFrom)
      );
    }
    if (filters?.dateTo) {
      filtered = filtered?.filter(calc => 
        new Date(calc?.createdAt) <= new Date(filters?.dateTo + 'T23:59:59')
      );
    }

    // Filter by project name
    if (filters?.projectName) {
      filtered = filtered?.filter(calc => 
        calc?.projectName?.toLowerCase()?.includes(filters?.projectName?.toLowerCase())
      );
    }

    // Filter by profit status
    if (filters?.profitStatus === 'profit') {
      filtered = filtered?.filter(calc => calc?.lucroLiquido > 0);
    } else if (filters?.profitStatus === 'loss') {
      filtered = filtered?.filter(calc => calc?.lucroLiquido <= 0);
    }

    setFilteredCalculations(filtered);
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este cálculo?')) return;

    try {
      await profitCalculationService?.deleteCalculation(id);
      await loadCalculations();
    } catch (err) {
      setError('Erro ao excluir cálculo: ' + err?.message);
    }
  };

  const handleViewDetails = (calculation) => {
    setSelectedCalculation(calculation);
    setShowDetailModal(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getProfitStatusColor = (lucroLiquido) => {
    return lucroLiquido > 0 ? 'text-success' : 'text-destructive';
  };

  const getProfitStatusBg = (lucroLiquido) => {
    return lucroLiquido > 0 ? 'bg-success/10' : 'bg-destructive/10';
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader userName={profile?.fullName || profile?.email} onSignOut={handleSignOut} />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <History className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Histórico de Cálculos</h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Visualize e gerencie os cálculos de lucro salvos
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/profit-calculator')}
              variant="default"
              className="hidden sm:flex"
            >
              Nova Simulação
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <CalculationFilters filters={filters} setFilters={setFilters} />

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Projeto
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Receita Total
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Total Gastos
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Lucro Líquido
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Margem %
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredCalculations?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <History className="h-12 w-12 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground">Nenhum cálculo encontrado</p>
                        <Button
                          onClick={() => navigate('/profit-calculator')}
                          variant="outline"
                          size="sm"
                          className="mt-2"
                        >
                          Criar Primeiro Cálculo
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCalculations?.map((calc) => (
                    <tr key={calc?.id} className="hover:bg-muted/50">
                      <td className="px-4 py-4 text-sm text-foreground whitespace-nowrap">
                        {formatDate(calc?.createdAt)}
                      </td>
                      <td className="px-4 py-4 text-sm text-foreground">
                        {calc?.projectName || 'Sem nome'}
                      </td>
                      <td className="px-4 py-4 text-sm text-foreground text-right font-medium">
                        {formatCurrency(calc?.totalObra)}
                      </td>
                      <td className="px-4 py-4 text-sm text-muted-foreground text-right">
                        {formatCurrency(calc?.totalGastos)}
                      </td>
                      <td className={`px-4 py-4 text-sm text-right font-semibold ${getProfitStatusColor(calc?.lucroLiquido)}`}>
                        {formatCurrency(calc?.lucroLiquido)}
                      </td>
                      <td className="px-4 py-4 text-sm text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getProfitStatusBg(calc?.lucroLiquido)} ${getProfitStatusColor(calc?.lucroLiquido)}`}>
                          {calc?.lucroLiquido > 0 ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          )}
                          {calc?.lucroPercent?.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${calc?.lucroLiquido > 0 ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
                          {calc?.lucroLiquido > 0 ? 'Lucro' : 'Prejuízo'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => handleViewDetails(calc)}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(calc?.id)}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <p>
            Exibindo <span className="font-medium text-foreground">{filteredCalculations?.length}</span> de{' '}
            <span className="font-medium text-foreground">{calculations?.length}</span> cálculos
          </p>
        </div>
      </div>

      {showDetailModal && selectedCalculation && (
        <CalculationDetailModal
          calculation={selectedCalculation}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCalculation(null);
          }}
        />
      )}
    </div>
  );
};

export default ProfitCalculationHistory;